'use client';

import React, { HTMLAttributes, useState } from 'react';

import { Locale } from '../../../i18n.config';
import parser from 'html-react-parser';
import { cn, getTranslation } from '@/lib/utils';
import { parseOptions } from '@/utils/parseOptions';
import { SingleCarouselCard } from '../SingleCarouselCard';
import './styles.css';
import Slider from 'react-slick';

type GuideItemWithHasPage = GuideItem & {hasPage: boolean}

type SectionData = Section & {
  guideItems?: GuideItemWithHasPage[]
};

interface ISingleCarouselSectionProps extends HTMLAttributes<HTMLDivElement> {
  lang: Locale;
  sectionData: SectionData;
  showPreview?: boolean;
  discoverMore: string;
  // guideSurroundingPage?: {
  //   pages: GuideSurroundingPage[];
  // };
}

const discoverSicilyUrl = {
  en: '/sicily/discover-sicily',
  it: '/it/sicilia/scopri-la-sicilia',
  de: '/de/sizilien/sizilien-entdecken',
  fr: '/fr/sicile/decouvrez-la-sicile',
};

export const SingleCarouselSection: React.FC<ISingleCarouselSectionProps> = ({
  sectionData,
  discoverMore,
  showPreview,
  lang,
  ...rest
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  const settings = {
    dots: true,
    dotsClass: 'carousel-dots',
    infinite: true,
    speed: 1000,
    ...( showPreview ? {
      centerPadding: '13.104%',
      centerMode: true,
    } : {}),
    arrows: true,
    initialSlide: 0,
    slidesToShow: 1,
    beforeChange: (currentSlide: number, nextSlide: number) => {
      setCurrentPage(nextSlide);
    },
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          centerMode: false,
          centerPadding: '0',
          infinite: true,
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1100,
        settings: {
          centerMode: false,
          centerPadding: '0',
          infinite: true,
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: 750,
        settings: {
          centerMode: false,
          centerPadding: '0',
          infinite: true,
          slidesToShow: 1,
        },
      },
    ],
    slidesToScroll: 1,
  };

  const items = (sectionData.guideItems || []).map((item: GuideItemWithHasPage, index) => {
    const itemInfo = item.surrounding?.id ? item.surrounding : item;
    const translate =
      (itemInfo as GuideItem).guideItemTranslation ||
      (itemInfo as Surrounding).translate;

    const translation = getTranslation(translate || [], lang);

    return (
      <SingleCarouselCard
        className={`group mx-auto ${currentPage === index && 'is-focused'} ${showPreview && 'show-preview desktop:h-[500px] desktop:before:hidden'}`}
        info={{
          banner: itemInfo.image
            ? `${process.env.NEXT_PUBLIC_S3_URI}/thumbs/1920w/${itemInfo.image.path}${itemInfo.image.filename}`
            : `${process.env.NEXT_PUBLIC_S3_URI}/assets/images/placeholder.jpg`,
          body: translation?.description || '',
          title: translation?.title || '',
          url: `${discoverSicilyUrl[lang]}/${translation?.slug || ''}`,
          buttonText: item.hasPage ? discoverMore : '',
        }}
        lang={lang}
        guideItem={item}
        key={item.id}
        sizes="(min-width: 1024px) 50vw, (min-width: 1200px) 75vw, 900px"
      />
    );
  });

  const translation = getTranslation(
    sectionData.guideSectionTranslation || [],
    lang,
  );

  return (
    <section
      className="flex flex-col px-4 max-w-[1680px] pt-[40px] mx-auto mb-[50px] tablet:mb-[80px] w-full tablet:px-10 desktop:px-20 z-10 items-center"
      {...rest}>
      <div className=" w-full px-0 flex flex-col justify-center mb-[60px]">
        <h2 className="mb-4 text-title font-title font-bold text-center">
          {translation.title}
        </h2>
        <div className="text-gray-850 text-lg leading-[1.5] text-center">
          {parser(translation?.description || '', parseOptions)}
        </div>
      </div>
      <div className="w-full desktop:pb-8">
        <Slider {...settings} className={cn("tour-carousel single-carousel w-full", {
          'show_preview': showPreview
        })}>
          {items}
        </Slider>
      </div>
    </section>
  );
};

export default SingleCarouselSection;
